import { PAUSED_UPLOAD_TTL_HOURS } from "@/components/UploadProgress/UploadProgressLoadingCard";

/**
 * Deterministic fixtures for the Tutorials demo card. Every state a contributor
 * can meet on a real upload, reproducible without touching the network.
 *
 * Shapes intentionally mirror what UploadProgressLoadingCard derives from
 * Redux + telemetry so the demo reads exactly like the live card.
 */

const MB = 1024 * 1024;
const HOUR_MS = 60 * 60 * 1000;

const files = (rows) =>
  rows.map(([name, sizeMb, pct, status], i) => ({
    sourceFileId: `demo-file-${i}`,
    originalName: name,
    sizeBytes: sizeMb * MB,
    loaded: Math.round((sizeMb * MB * pct) / 100),
    status,
  }));

const baseFiles = (activeStatus) =>
  files([
    ["A001_C003_0817_interview.mov", 812, 100, "uploaded"],
    ["A001_C004_0817_broll.mov", 1240, 46, activeStatus],
    ["A001_C005_0817_cutaways.mov", 655, 0, "queued"],
    ["A001_C006_0817_ambience.mov", 210, 0, "queued"],
  ]);

const PACKAGE_NAME = "City Hall press conference — 17 Aug";

export const DEMO_STATE_ORDER = [
  "uploading",
  "slow",
  "stalled",
  "retrying",
  "autoPaused",
  "pausedByUser",
  "interrupted",
  "anotherDevice",
  "finalizing",
  "completed",
];

export const DEMO_STATES = {
  uploading: {
    key: "uploading",
    label: "Uploading",
    chip: "Healthy",
    packageName: PACKAGE_NAME,
    status: "UPLOADING",
    pct: 47,
    remainingSeconds: 14 * 60,
    uploadedText: "1.38 GB of 2.85 GB",
    speedText: "3.2 MB/s",
    files: baseFiles("uploading"),
    explain:
      "Bytes are flowing. The speed row and the growing bar are the only signals you need. Files go a few at a time; the rest wait as Queued.",
  },
  slow: {
    key: "slow",
    label: "Slow connection",
    chip: "Slow",
    packageName: PACKAGE_NAME,
    status: "UPLOADING",
    pct: 48,
    remainingSeconds: 96 * 60,
    uploadedText: "1,401,872 KB of 2.85 GB",
    speedText: "180 KB/s",
    telemetryStatus: "slow",
    networkMessage: "Upload may take longer than usual due to a slow connection.",
    networkSeverity: "warning",
    files: baseFiles("uploading"),
    explain:
      "Nothing is wrong — the uploader adapts to whatever bandwidth you have. The uploaded counter switches to KB so you can see it moving even on a weak signal.",
  },
  stalled: {
    key: "stalled",
    label: "Reconnecting",
    chip: "Auto-retry",
    packageName: PACKAGE_NAME,
    status: "UPLOADING",
    pct: 48,
    remainingSeconds: null,
    uploadedText: "1.39 GB of 2.85 GB",
    speedText: "0 B/s",
    stallStatus: "stalled",
    files: baseFiles("retrying"),
    explain:
      "No bytes for a while (Wi-Fi dropped, tunnel, hotspot switch). The uploader is already retrying by itself and continues from the last chunk the moment the network is back. Nothing to press.",
  },
  retrying: {
    key: "retrying",
    label: "Reconnecting…",
    chip: "Auto-retry",
    packageName: PACKAGE_NAME,
    status: "UPLOADING",
    pct: 48,
    remainingSeconds: null,
    uploadedText: "1.39 GB of 2.85 GB",
    speedText: "0 B/s",
    stallStatus: "reconnecting",
    networkMessage: "You appear to be offline. Upload cannot continue.",
    networkSeverity: "danger",
    files: baseFiles("retrying"),
    explain:
      "Fully offline. The card says so and keeps the connection attempt alive. Already-uploaded chunks are safe on the server; nothing is lost.",
  },
  autoPaused: {
    key: "autoPaused",
    label: "Auto-paused",
    chip: "Paused by app",
    packageName: PACKAGE_NAME,
    status: "PAUSED",
    pct: 48,
    remainingSeconds: null,
    uploadedText: "1.39 GB of 2.85 GB",
    telemetryStatus: "paused",
    pausedAt: () => Date.now() - 20 * 60 * 1000,
    files: baseFiles("paused"),
    explain: `The connection stayed bad for a while, so the app paused the upload to protect it. It will resume itself when the network returns (up to 3 times); if that fails you get one small "Resume?" popup. You can also press the play icon any time within ${PAUSED_UPLOAD_TTL_HOURS} hours.`,
  },
  pausedByUser: {
    key: "pausedByUser",
    label: "Paused by you",
    chip: "Paused",
    packageName: PACKAGE_NAME,
    status: "PAUSED",
    pct: 48,
    remainingSeconds: null,
    uploadedText: "1.39 GB of 2.85 GB",
    telemetryStatus: "paused",
    pausedAt: () => Date.now() - 1 * HOUR_MS,
    files: baseFiles("paused"),
    explain: `You pressed the pause icon — maybe to move to better Wi-Fi or close the laptop. Come back and press play. The card shows how long the paused upload is kept (${PAUSED_UPLOAD_TTL_HOURS} hours); already-sent chunks are never re-sent.`,
  },
  interrupted: {
    key: "interrupted",
    label: "After a reload / closed tab",
    chip: "Continue",
    packageName: PACKAGE_NAME,
    status: "PAUSED",
    pct: 48,
    remainingSeconds: null,
    uploadedText: "1.39 GB of 2.85 GB",
    telemetryStatus: "paused",
    pausedAt: () => Date.now() - 2 * HOUR_MS,
    needsRepick: true,
    statusMessage: "Upload interrupted by page reload.",
    files: baseFiles("paused"),
    explain:
      "Closed the tab or the browser? Open the app again — the card is back. Browsers don't let a page keep your files after a reload, so select the same files or folder once more; the uploader skips every chunk that already arrived.",
  },
  anotherDevice: {
    key: "anotherDevice",
    label: "Started on another device",
    chip: "Another device",
    packageName: PACKAGE_NAME,
    remote: {
      uploadId: "demo-remote-upload",
      packageName: PACKAGE_NAME,
      status: "PAUSED",
      bytesUploaded: 1.39 * 1024 * MB,
      totalSize: 2.85 * 1024 * MB,
      filesCount: 4,
    },
    explain:
      "You started this package on another computer or in another browser. Only one upload runs per account at a time — press Take over here to continue it from this device instead of cancelling and starting again.",
  },
  finalizing: {
    key: "finalizing",
    label: "Finalizing on server",
    chip: "Almost done",
    packageName: PACKAGE_NAME,
    status: "UPLOADING",
    pct: 100,
    remainingSeconds: null,
    uploadedText: "2.85 GB of 2.85 GB",
    telemetryStatus: "finalizing",
    isFinalizing: true,
    files: files([
      ["A001_C003_0817_interview.mov", 812, 100, "uploaded"],
      ["A001_C004_0817_broll.mov", 1240, 100, "uploaded"],
      ["A001_C005_0817_cutaways.mov", 655, 100, "uploaded"],
      ["A001_C006_0817_ambience.mov", 210, 100, "uploaded"],
    ]),
    explain:
      "All bytes are on the server; it is assembling and checking the files. This can take a minute for big packages. You can close the tab — it finishes without you.",
  },
  completed: {
    key: "completed",
    label: "Done",
    chip: "Done",
    packageName: PACKAGE_NAME,
    status: "COMPLETED",
    pct: 100,
    remainingSeconds: null,
    uploadedText: "2.85 GB of 2.85 GB",
    files: files([
      ["A001_C003_0817_interview.mov", 812, 100, "uploaded"],
      ["A001_C004_0817_broll.mov", 1240, 100, "uploaded"],
      ["A001_C005_0817_cutaways.mov", 655, 100, "uploaded"],
      ["A001_C006_0817_ambience.mov", 210, 100, "uploaded"],
    ]),
    explain: "The package is delivered and will show up under Uploads.",
  },
  canceled: {
    key: "canceled",
    label: "Cancelled",
    chip: "Deleted",
    packageName: PACKAGE_NAME,
    status: "CANCELED",
    pct: 48,
    remainingSeconds: null,
    uploadedText: "1.39 GB of 2.85 GB",
    statusMessage: "Your upload has been canceled. Everything already uploaded was deleted on the server.",
    files: [],
    explain:
      "Cancel deletes the package on the server, including every chunk that already arrived. To deliver the footage now you start again from zero — on the same connection.",
  },
};

export const getDemoState = (key) => DEMO_STATES[key] || DEMO_STATES.uploading;
